import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import HoldMusicAPI from '@frontend/api-hold-music';
import { phoneAuditsQueryKeys } from '@frontend/api-phone-audits';
import { CalloutDialog } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { SurfaceHeader } from '@frontend/surface-header';
import { pendo } from '@frontend/tracking';
import { useEligibility } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import {
  Button,
  Chip,
  ContentLoader,
  ModalControlModalProps,
  NakedUl,
  RadioField,
  Text,
  Tray,
  styles,
  useAlert,
  useFormField,
  useModalControl,
  usePopoverDialog,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { logError } from '../../utils/loggers';
import { buildAudioLibraryPath } from '../../utils/media-path';
import { AudioPicker } from '../audio-picker/audio-picker';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';
import { SeeHistoryButton } from '../common/see-history-button';
import { SettingsCard } from '../common/settings-card';
import { PhoneSystemHistoryTrackingIds } from '../phone-system-history/utils/tracking-ids';
import { LocationHoldMusic, LocationHoldMusicUpdateRequest } from './types';

export const HoldMusicLocationList = ({
  tenantLocation,
  locationHoldMusic,
  historyProps,
}: {
  tenantLocation: SettingsTenantLocation;
  locationHoldMusic: LocationHoldMusic[];
  historyProps?: {
    activeLocationId?: string;
    onHistoryClick: (locationId: string) => void;
  };
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();
  const { getScopeName } = useAppScopeStore();
  const [trayData, setTrayData] = useState<{
    label: string;
    type: 'single' | 'multi';
    mediaId: string;
    locationIds: string[];
  }>({
    label: '',
    type: 'single',
    mediaId: '',
    locationIds: [],
  });

  // Callout logic (only when historyProps is provided)
  const { shouldShow, disableEligibility } = useEligibility({
    featureId: 'phone-system-history-callout-hold-music',
    defaultCriteria: {
      strategy: 'any',
      conditions: [
        { type: 'days-shown', maxDaysToShow: 2 },
        { type: 'date-range', startDate: '2026-07-07', endDate: '2026-09-22' },
      ],
    },
  });

  const {
    getDialogProps,
    close: closeCalloutDialog,
    open: openCalloutDialog,
    refs,
  } = usePopoverDialog({
    placement: 'left',
    interactionOptions: {
      dismiss: {
        outsidePress: false,
        escapeKey: false,
      },
    },
  });

  // Auto-open callout when eligible and no history tray is active
  useEffect(() => {
    if (historyProps && shouldShow && !historyProps.activeLocationId) {
      openCalloutDialog();
    } else {
      closeCalloutDialog();
    }
  }, [shouldShow, historyProps?.activeLocationId, openCalloutDialog, closeCalloutDialog, historyProps]);

  const handleHistoryClick = useCallback(
    (locationId: string) => {
      if (shouldShow) {
        disableEligibility();
        closeCalloutDialog();
      }
      historyProps?.onHistoryClick(locationId);
    },
    [disableEligibility, historyProps, shouldShow, closeCalloutDialog]
  );

  const handleGotItClick = useCallback(() => {
    disableEligibility();
    closeCalloutDialog();
  }, [disableEligibility, closeCalloutDialog]);

  // Sort location hold music alphabetically by location name
  const sortedLocationHoldMusic = useMemo(
    () => [...locationHoldMusic].sort((a, b) => getScopeName(a.locationId).localeCompare(getScopeName(b.locationId))),
    [locationHoldMusic, getScopeName]
  );

  if (!locationHoldMusic.length) {
    return (
      <Text size='medium' color='subdued'>
        {t('No hold music set for this phone tenant.')}
      </Text>
    );
  }

  const hasMultipleLocations = locationHoldMusic.length > 1;

  return (
    <>
      <NakedUl css={{ maxWidth: 860, display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        {sortedLocationHoldMusic.map((item, index) => {
          const isActive = historyProps?.activeLocationId === item.locationId;
          const isFirstCard = index === 0;

          return (
            <li
              key={item.locationId}
              onClickCapture={() => {
                setTrayData({
                  label: getScopeName(item.locationId),
                  type: 'single',
                  mediaId: item.mediaItemId,
                  locationIds: [item.locationId],
                });
              }}
            >
              <SettingsCard>
                <div css={cardContainerStyle}>
                  <div css={cardGridStyle(hasMultipleLocations)}>
                    {hasMultipleLocations && (
                      <Chip.SingleChip css={{ justifyContent: 'start', gridArea: 'location' }}>
                        {getScopeName(item.locationId)}
                      </Chip.SingleChip>
                    )}
                    <div
                      css={[
                        styles.truncate,
                        css`
                          grid-area: track-title;
                        `,
                      ]}
                    >
                      <Text size='medium' color='subdued'>
                        {t('Track Title')}
                      </Text>
                      <Text css={styles.truncate} size='large'>
                        {item.name}
                      </Text>
                    </div>
                    <div css={audioAreaStyle}>
                      <CachedAudioScrubber
                        singlePlayer
                        filePath={buildAudioLibraryPath({
                          media: {
                            id: item.mediaItemId,
                            isGlobal: false,
                            path: item.downloadUrl,
                          },
                        })}
                        mediaId={item.mediaItemId}
                      />
                    </div>
                    {historyProps ? (
                      <div css={actionsAreaStyle}>
                        <div
                          ref={isFirstCard ? refs.setReference : undefined}
                          css={{ display: 'flex', alignItems: 'center' }}
                        >
                          <SeeHistoryButton
                            onClick={() => handleHistoryClick(item.locationId)}
                            isActive={isActive}
                            trackingId={`${PhoneSystemHistoryTrackingIds.seeHistoryButtons.holdMusic}-see-history-button`}
                            variant='tertiary'
                            showLabel={true}
                          />
                          {isFirstCard && shouldShow && (
                            <CalloutDialog
                              color='seaweed'
                              popoverDialogProps={getDialogProps()}
                              badge='new'
                              title={
                                <Text weight='bold' size='medium' as='span'>
                                  {t('See how your phone settings have changed over time')}
                                </Text>
                              }
                              cardStyles={{ width: '340px' }}
                              containerStyles={{ overflow: 'visible' }}
                              description={
                                <Text weight='regular' size='medium' as='span'>
                                  {t(
                                    'With Phone System History, see how and when your phone system settings have changed. We will be releasing history related to more settings in the near future.'
                                  )}
                                </Text>
                              }
                              actions={
                                <Button
                                  variant='tertiary'
                                  color='primary'
                                  onClick={handleGotItClick}
                                  trackingId={`${PhoneSystemHistoryTrackingIds.seeHistoryButtons.holdMusic}-see-history-callout-got-it-button`}
                                >
                                  {t('Got it')}
                                </Button>
                              }
                            />
                          )}
                        </div>
                        <Button
                          variant='secondary'
                          onClick={() => {
                            triggerProps.onClick();
                          }}
                        >
                          {t('Manage')}
                        </Button>
                      </div>
                    ) : (
                      <div css={actionsAreaStyle}>
                        <Button
                          variant='secondary'
                          onClick={() => {
                            triggerProps.onClick();
                          }}
                        >
                          {t('Manage')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SettingsCard>
            </li>
          );
        })}
      </NakedUl>
      <ManageHoldMusicTray
        key={trayData.label}
        tenantLocation={tenantLocation}
        modalProps={modalProps}
        data={trayData}
      />
    </>
  );
};

// Container wrapper for responsive behavior based on container width
const cardContainerStyle = css`
  container-type: inline-size;
  container-name: hold-music-card;
`;

const audioAreaStyle = css`
  grid-area: audio;
  min-width: 0;
`;

const actionsAreaStyle = css`
  grid-area: actions;
  display: flex;
  gap: var(--fabric-space-gap-xs);
  justify-self: end;
`;

// Responsive grid layout for hold music cards.
// Uses container queries - layout adjusts based on actual card width.
// > 500px: Single row with all elements side by side
// 360-500px: Stacked layout (track-title next to audio, actions below) with fixed track-title width for alignment
// < 360px: Completely vertical stack
const cardGridStyle = (hasMultipleLocations: boolean) => {
  return css`
    display: grid;
    grid-template-areas: ${hasMultipleLocations
      ? `'location track-title audio actions'`
      : `'track-title audio actions'`};
    grid-template-columns: ${hasMultipleLocations
      ? 'minmax(80px, 120px) minmax(100px, 140px) minmax(180px, 234px) auto'
      : 'minmax(100px, 140px) minmax(180px, 234px) auto'};
    align-items: center;
    gap: var(--fabric-space-gap-md);

    /* When card is narrower, stack to prevent button overflow and ensure alignment */
    @container hold-music-card (max-width: 650px) {
      grid-template-areas: ${hasMultipleLocations
        ? `'location location' 'track-title audio' 'actions actions'`
        : `'track-title audio' 'actions actions'`};
      grid-template-columns: 160px 1fr;
    }

    /* When very narrow, stack completely */
    @container hold-music-card (max-width: 360px) {
      grid-template-areas: ${hasMultipleLocations
        ? `'location' 'track-title' 'audio' 'actions'`
        : `'track-title' 'audio' 'actions'`};
      grid-template-columns: 1fr;
    }
  `;
};

export const ManageHoldMusicTray = memo(
  ({
    tenantLocation,
    modalProps,
    data,
  }: {
    tenantLocation: SettingsTenantLocation;
    modalProps: ModalControlModalProps;
    data: { label: string; type: 'single' | 'multi'; mediaId?: string; locationIds: string[] };
  }) => {
    const { t } = useTranslation('phone');
    const alerts = useAlert();
    const queryClient = useQueryClient();

    const { data: standardMedia = [], isLoading } = useQuery({
      // No need for tenantId here
      queryKey: queryKeys.settings.listSystemMedia(),
      queryFn: () => {
        return HoldMusicAPI.ListSystem({});
      },
      select: (response) =>
        response?.systemMedia?.map((media) => ({
          id: media.mediaItemId,
          name: media.name,
          path: media.downloadUrl,
        })),
      meta: {
        error: {
          message: 'ManageHoldMusicTray:standardMediaQuery | Failed to fetch standard media',
          data: {},
        },
      },
    });

    const update = useMutation({
      mutationFn: (payload: LocationHoldMusicUpdateRequest) => {
        return HoldMusicAPI.Update(payload);
      },
      onSuccess: (_data, payload) => {
        alerts.success(t('Hold Music successfully updated.'));
        pendo.track('phone-settings-hold-music-updated', {
          mediaType: payload.system ? 'system' : 'custom',
          mediaId: payload.mediaItemId,
        });
      },
      onError: (error) => {
        alerts.error(t('System failed to update Hold Music. Please try again.'));
        logError('ManageHoldMusicTray:updateMutation | Failed to update hold music', {
          data: { phoneTenantId: tenantLocation.phoneTenantId, error },
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries([tenantLocation.phoneTenantId, ...queryKeys.settings.listLocationHoldMusic()]);
        queryClient.invalidateQueries(
          phoneAuditsQueryKeys.searchAuditEventsByTenant(tenantLocation.phoneTenantId ?? '')
        );
        modalProps.onClose();
      },
    });

    const targetStandardMediaItem = data?.mediaId ? standardMedia.find((item) => item.id === data?.mediaId) : undefined;
    let fieldValues: { radio: string | undefined; picker: string | undefined } = {
      radio: undefined,
      picker: undefined,
    };
    if (data.mediaId !== 'none') {
      fieldValues = targetStandardMediaItem
        ? {
            radio: targetStandardMediaItem.id,
            picker: undefined,
          }
        : { radio: 'custom', picker: data?.mediaId || '' };
    }

    const field = useFormField({ type: 'radio', value: fieldValues.radio }, [fieldValues.radio]);
    const audioPickerField = useFormField({ type: 'dropdown', value: fieldValues.picker }, [fieldValues.picker]);

    let chip;
    if (data?.type === 'multi') {
      chip = <Chip.MultiChip>{data.label}</Chip.MultiChip>;
    } else if (data?.type === 'single') {
      chip = <Chip.SingleChip>{data.label}</Chip.SingleChip>;
    }

    return (
      <Tray mountTarget='[data-settings-modal-content]' {...modalProps} width='xlarge'>
        <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-4, -4, 0, -4), width: 'calc(100% + 64px)' }}>
          <SurfaceHeader.Row>
            <SurfaceHeader.Title>{t('Manage Hold Music')}</SurfaceHeader.Title>
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon iconName='x' onClick={modalProps.onClose} />
            </SurfaceHeader.Actions>
          </SurfaceHeader.Row>
          <SurfaceHeader.Body>
            {chip}
            {t('Select the music that will play when your callers are on hold.')}
          </SurfaceHeader.Body>
        </SurfaceHeader.Root>
        <Tray.Body>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '60% 40%',
              borderBottom: `1px solid ${theme.colors.neutral20}`,
              padding: theme.spacing(2),
              alignItems: 'center',
            }}
          >
            <Text size='medium' color='subdued'>
              {t('Track Title')}
            </Text>
            <Text size='medium' color='subdued'>
              {t('Audio')}
            </Text>
          </div>
          {isLoading ? (
            <ContentLoader show message={t('Loading Audio Files')} />
          ) : (
            <RadioField {...field} name='hold-music' label=''>
              {/* Standard Media */}
              <>
                {standardMedia.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '60% 240px',
                      borderBottom: `1px solid ${theme.colors.neutral20}`,
                      padding: theme.spacing(1, 2),
                      alignItems: 'center',
                    }}
                  >
                    <RadioField.Radio css={{ margin: 0 }} value={item.id}>
                      {item.name}
                    </RadioField.Radio>

                    <div>
                      <CachedAudioScrubber
                        filePath={buildAudioLibraryPath({
                          media: { id: item.id, path: item.path, isGlobal: true },
                        })}
                        mediaId={item.id}
                      />
                    </div>
                  </div>
                ))}
              </>
              {/* Custom Media - Audio Picker */}
              <div
                key={'custom'}
                style={{
                  display: 'grid',
                  gridTemplateRows: 'repeat(auto-fit, 40px)',
                  gridTemplateColumns: '60% 240px',
                  gridTemplateAreas: field.value === 'custom' ? '"radio ." "picker picker"' : '"radio ."',
                  rowGap: theme.spacing(1),
                  borderBottom: `1px solid ${theme.colors.neutral20}`,
                  padding: theme.spacing(1, 2),
                  alignItems: 'center',
                }}
              >
                <RadioField.Radio css={{ margin: 0, gridArea: 'radio' }} value='custom'>
                  {t('Custom Audio File')}
                </RadioField.Radio>

                <div
                  style={{
                    gridArea: 'picker',
                    // width of radio + margin
                    paddingLeft: 28,
                    display: field.value === 'custom' ? 'block' : 'none',
                  }}
                >
                  <AudioPicker
                    widths={{ field: '200px', scrubber: '240px' }}
                    allowedOptions={{
                      standard: false,
                      custom: true,
                      mailbox: false,
                      add: true,
                    }}
                    name='audio-picker'
                    field={audioPickerField}
                    tenantId={tenantLocation.phoneTenantId ?? ''}
                    css={{ justifyContent: 'space-between' }}
                    hideTTS={true}
                  />
                </div>
              </div>
            </RadioField>
          )}
        </Tray.Body>
        <Tray.Actions
          onPrimaryClick={() => {
            if (!field.value) {
              return;
            }

            const payload: LocationHoldMusicUpdateRequest = {
              locationIds: data.locationIds,
              tenantId: tenantLocation.phoneTenantId ?? '',
              mediaItemId: field.value === 'custom' ? audioPickerField.value : field.value,
              system: field.value !== 'custom',
            };
            update.mutate(payload);
          }}
          disabledPrimary={!field.value}
          primaryLabel={t('Save')}
          onSecondaryClick={modalProps.onClose}
          secondaryLabel={t('Cancel')}
        />
      </Tray>
    );
  }
);

ManageHoldMusicTray.displayName = 'ManageHoldMusicTray';
